import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useUpdatePaymentMethod } from '@/hooks/api/2320_update_payment_method';
import { toastApiError, toastError } from '@/lib/toast';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { usePaymentInfoInvalidate } from '@/hooks/api/2280_workspace_payment_info';
import { useSetupIntentClientSecret } from '@/hooks/api/2321_setup_intent_client_secret';
import { StripeElementsOptionsClientSecret } from '@stripe/stripe-js/dist/stripe-js/elements-group';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

type PaymentProps = {
  setPaymentInfoUpdated: Dispatch<SetStateAction<boolean>>;
};

export const StripePaymentElement = ({ setPaymentInfoUpdated }: PaymentProps) => {
  const { _w_, me } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { data, isPending, error } = useSetupIntentClientSecret({ identifier: _w_!.identifier });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  if (data == null) return <></>;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        // mode: 'setup',
        // currency: 'jpy',
        appearance: { theme: 'stripe' },
        clientSecret: data.client_secret,
      }}
    >
      <Payment setPaymentInfoUpdated={setPaymentInfoUpdated} />
    </Elements>
  );
};

const Payment = ({ setPaymentInfoUpdated }: PaymentProps) => {
  const { _w_, me } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { mutate: updatePaymentInfoMutate, data, isPending, error } = useUpdatePaymentMethod();
  const { invalidate: invalidatePaymentInfo } = usePaymentInfoInvalidate({ identifier: _w_!.identifier });
  const [setupIntent, setSetupIntent] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setSetupIntent(searchParams.get('setup_intent'));
  }, [searchParams]);

  useEffect(() => {
    if (setupIntent) {
      setShowLoading(true);
      updatePaymentInfoMutate({ identifier: _w_!.identifier, setup_intent_id: setupIntent });
    }
  }, [setupIntent]);

  useEffect(() => {
    if (data) {
      invalidatePaymentInfo();
      setPaymentInfoUpdated(true);
    }
    if (error) {
      setSetupIntent(null);
    }
  }, [data, error]);

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const stripe = useStripe();
  const elements = useElements();
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return null;

    setShowLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setShowLoading(false);
      return;
    }

    const { error: confirmSetupError } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: location.href.split('?')[0],
      },
    });

    if (confirmSetupError) {
      toastError(confirmSetupError.message || 'エラーが発生しました');
      setShowLoading(false);
      return;
    }
  };

  if (setupIntent) return <></>;

  return (
    <form onSubmit={(e) => handleSubmit(e)}>
      <div className="form-group mt-4">
        <label>請求先</label>
        <div className="form-control border-0 ps-1">
          <AddressElement options={{ display: {}, mode: 'billing', allowedCountries: ['JP'] }} />
        </div>
      </div>

      <div className="form-group mt-4">
        <label>支払方法</label>
        <PaymentElement
          options={{
            layout: 'accordion',
            fields: { billingDetails: { address: { country: 'never' } } },
          }}
        />
      </div>

      <div className="card-footer d-flex align-items-center justify-content-end pt-4 pe-0 pb-0">
        <Link to={`/w/${_w_!.identifier}/setting`} className="btn btn-outline-secondary me-2">
          戻る
        </Link>
        <button className="btn bg-gradient-dark btn-icon">
          <i className="fas fa-check-circle"></i>変更を確定する
        </button>
      </div>
    </form>
  );
};
